import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, Observable, Subject, timer } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import moment from 'moment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { BannerService } from '@nimbus-assistant/services/banner.service';
import { IStateBanner, StateBannerType } from '@nimbus-assistant/interfaces/banner';
import { LoggingService } from '@nimbus-assistant/services/logging.service';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import { BackgroundTaskFactory } from '@nimbus-library/features/background-task/background-task.factory';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { UserTpCapacityButtonComponent } from '@nimbus-global/features/user-tasks-capacity/components/user-tp-capacity-button/user-tp-capacity-button.component';
import { UserNotReadyReason, UserNotReadyReasonTooltipMapper } from '@nimbus-library/features/user-state/models/not-ready-reason';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, SharedUIModule, UserTpCapacityButtonComponent],
})
export class BannerComponent implements OnInit {
  private readonly _stopProgressBar$ = new Subject<void>();

  public readonly ICONS = ICONS;
  public readonly IconWeight = IconWeight;
  public readonly StateBannerType = StateBannerType;
  public readonly TranslationKeys = TranslationKeys;
  public readonly UserNotReadyReason = UserNotReadyReason;
  public readonly UserNotReadyReasonTooltipMapper = UserNotReadyReasonTooltipMapper;

  public readonly banner$ = this.taskBannerService.banner$;
  public currentMoment$: Observable<moment.Moment>;

  public extendInProgress$ = new BehaviorSubject<boolean>(false);
  public extendDisabled$ = new BehaviorSubject<boolean>(false);
  public earlyEndDisabled$ = new BehaviorSubject<boolean>(false);

  public isExtendButtonDisabled$: Observable<boolean> = combineLatest([this.extendInProgress$, this.extendDisabled$]).pipe(
    map(([extendInProgress, extendDisabled]) => extendInProgress || extendDisabled),
  );

  public isStopButtonDisabled$: Observable<boolean> = combineLatest([this.earlyEndDisabled$, this.banner$]).pipe(
    map(([earlyEndDisabled, banner]) => earlyEndDisabled || !banner?.allowToStop),
  );

  public constructor(
    private readonly taskBannerService: BannerService,
    private readonly backgroundTaskFactory: BackgroundTaskFactory,
    private readonly loggingService: LoggingService,
  ) {}

  public ngOnInit(): void {
    this.currentMoment$ = this.backgroundTaskFactory.getServerWatches();
    this.banner$.pipe(untilDestroyed(this)).subscribe((banner) => {
      this.resetBanner(banner);
    });
  }

  public extend(): void {
    const banner = this.banner$.value;
    if (banner?.extendHandler) {
      banner.extendHandler();
    }
    this.extendInProgress$.next(true);
  }

  public stop(): void {
    this.loggingService.Info('Banner stop called');
    const banner = this.banner$.value;
    if (banner?.stopHandler) {
      banner.stopHandler(!this.earlyEndDisabled$.value);
    }
  }

  private resetBanner(banner: IStateBanner): void {
    this._stopProgressBar$.next();
    this.extendInProgress$.next(false);
    this.extendDisabled$.next(false);
    this.earlyEndDisabled$.next(false);

    const msCountDown = banner?.countDownTo?.diff(moment(), 'ms');
    if (!msCountDown || msCountDown < 0) {
      return;
    }

    const initialDelay = 10;
    const msStep = 300;
    const extendGap = 5000;
    timer(initialDelay, msStep)
      .pipe(takeUntil(this._stopProgressBar$), untilDestroyed(this))
      .subscribe(() => {
        const msLeft = banner.countDownTo.diff(moment(), 'ms');

        if (msLeft < extendGap) {
          this.extendDisabled$.next(true);
          this.earlyEndDisabled$.next(true);
        }

        if (msLeft < 0) {
          this.loggingService.Info('Banner stop called by timer end');
          return this.stop();
        }

        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        const passPercent = Math.floor((100 * msLeft) / (banner.countDownLeftSeconds * 1000));

        const progressBar = document.getElementById('task-progress-bar');
        if (progressBar) {
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          progressBar.style.width = `${100 - passPercent}%`;
        }
      });
  }
}
